import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useData } from '../../data/DataProvider';
import StatusPill from '../../shared/components/StatusPill';
import JobPhotoStrip from '../../shared/components/JobPhotoStrip';
import EmptyState from '../../shared/components/EmptyState';
import Skeleton from '../../shared/components/Skeleton';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';

const day = (naiveLocal) => (naiveLocal ? String(naiveLocal).slice(0, 10) : '—');
const clock = (naiveLocal) => (naiveLocal ? String(naiveLocal).slice(11, 16) : '');

// Job detail page (PLAN_admin_portal_phase1.md §3): URL-addressable view of
// a single job with client + property links, schedule, site, photos and any
// linked billing documents. Edit goes to /admin/jobs/:id/edit (JobForm page).
export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, ready } = useData();

  if (!ready) return <div className="standalone-page"><Skeleton rows={8} /></div>;

  const job = data.jobs.find((item) => item.id === id);
  if (!job) {
    return (
      <div className="standalone-page">
        <EmptyState
          title="Job not found"
          message="It may have been deleted, or the link is stale."
          action="Back to jobs"
          onAction={() => navigate('/admin/jobs')}
        />
      </div>
    );
  }

  const client = data.clients.find((item) => item.id === job.client_id);
  const property = data.properties.find((item) => item.id === job.property_id);
  const linkedInvoices = data.invoices.filter((invoice) => invoice.linked_job_id === job.id);
  const siteAddress = [job.site_line1, job.site_line2, job.site_city, job.site_postcode, job.site_country].filter(Boolean).join(', ');

  return (
    <div className="standalone-page">
      <header className="simple-header">
        <div>
          <span className="eyebrow">Jobs</span>
          <h1>{job.title}</h1>
          <p className="field-hint">
            {client ? <Link className="link-button" to={`/admin/clients/${client.id}`}>{client.name}</Link> : 'Unknown client'}
            {property ? <> · {property.name}</> : null}
          </p>
        </div>
        <div className="header-actions">
          <StatusPill status={job.status} />
          <button className="primary-button" onClick={() => navigate(`/admin/jobs/${job.id}/edit`)}>Edit job</button>
        </div>
      </header>

      <div className="overview-grid">
        <section className="panel span-two">
          <div className="panel-head"><h3>Schedule</h3></div>
          <dl className="detail-list">
            <dt>Scheduled</dt>
            <dd>{job.scheduled_start ? `${day(job.scheduled_start)} at ${clock(job.scheduled_start)}` : 'Not scheduled'}</dd>
            <dt>Ends</dt>
            <dd>{job.scheduled_end ? `${day(job.scheduled_end)} at ${clock(job.scheduled_end)}` : '—'}</dd>
            <dt>Completed</dt>
            <dd>{day(job.date_completed)}</dd>
            <dt>Engineer</dt>
            <dd>{job.engineer || 'Unassigned'}</dd>
          </dl>
        </section>

        <section className="panel">
          <div className="panel-head"><h3>Site</h3></div>
          <dl className="detail-list">
            <dt>Address</dt>
            <dd>{siteAddress || 'No site address recorded'}</dd>
            {property ? (
              <>
                <dt>Property</dt>
                <dd>{property.name}{property.address ? <small className="field-hint">{property.address}</small> : null}</dd>
              </>
            ) : null}
          </dl>
        </section>

        {linkedInvoices.length > 0 && (
          <section className="panel">
            <div className="panel-head"><h3>Billing documents</h3></div>
            <div className="invoice-list">
              {linkedInvoices.map((invoice) => (
                <div className="invoice-row" key={invoice.id}>
                  <div><strong>{invoice.number}</strong><span>{invoice.title}</span></div>
                  <span>{invoice.status}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {job.description && (
          <section className="panel">
            <div className="panel-head"><h3>Description</h3></div>
            <p>{job.description}</p>
          </section>
        )}

        {job.notes && (
          <section className="panel">
            <div className="panel-head"><h3>Notes</h3></div>
            <p>{job.notes}</p>
          </section>
        )}

        {job.photos?.length > 0 && (
          <section className="panel span-two">
            <div className="panel-head"><h3>Photos ({job.photos.length})</h3></div>
            <JobPhotoStrip job={job} />
          </section>
        )}
      </div>

      <p className="field-hint">
        <Link className="back-link" to="/admin/jobs"><SafeIcon icon={Icons.ArrowLeft} /> Back to jobs</Link>
      </p>
    </div>
  );
}
